import React, { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';
import { useEffect } from 'react';

const AddUpdateLocationPrimaryUsageTypeModal = ({ onClose, refetch, primaryUsageTypeId = null, initialData = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [primaryUsageTypes, setPrimaryUsageTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  // Form state
  const [formData, setFormData] = useState({
    primaryUsage: initialData?.primary_uage || '',
    primaryUsageTypeName: initialData?.primary_usage_type || '',
  });

  const validate = (values) => {
    const newErrors = {};

    // Primary Usage Type validation
    if (!values.primaryUsage.trim()) {
      newErrors.primaryUsage = 'Primary usage is required';
    }

    // Primary Usage Type Name validation
    if (!values.primaryUsageTypeName.trim()) {
      newErrors.primaryUsageTypeName = 'Primary usage type name is required';
    } else if (values.primaryUsageTypeName.length < 2) {
      newErrors.primaryUsageTypeName = 'Primary usage type name must be at least 2 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedPrimaryUsageTypeObj = primaryUsageTypes.find(primaryUsageType => primaryUsageType.primary_uage === formData.primaryUsage);
      const payload = {
        primaryUsageId: selectedPrimaryUsageTypeObj?.id,
        primaryUsageTypeId: primaryUsageTypeId || 0,
        ...formData
      };
      const response = await api.post('/admin/CreateUpdateLocationPrimaryUsageType', payload);
      if (response.data.status === '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
        onClose();
        refetch?.();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const getPrimaryUsageType = async () => {
    try {
      const response = await api.get('/admin/getPrimaryUsage');
      if (response.data.data) {
        setPrimaryUsageTypes(response.data.data);
      }
    } catch (err) {
      dispatch(showNotification({ 
        message: 'Failed to fetch Primary Usage Types', 
        type: "error" 
      }));
    }
  };

  useEffect(() => {
    getPrimaryUsageType();
  }, []);

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="primaryUsage" className="text-sm font-medium block">Primary Usage</label>
            <FormControl fullWidth size="small">
              <Select
                labelId="primaryUsage-label"
                id="primaryUsage"
                name="primaryUsage"
                value={formData.primaryUsage}
                onChange={handleInputChange}
                disabled={isSubmitting}
                error={Boolean(errors.primaryUsage)}
                className={'w-full'}
              >
                {primaryUsageTypes.map((primaryUsage) => (
                  <MenuItem key={primaryUsage.id} value={primaryUsage.primary_uage}>
                    {primaryUsage.primary_uage}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.primaryUsageType && (
              <p className="text-sm text-red-500 mt-1">{errors.primaryUsageType}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="primaryUsageTypeName" className="text-sm font-medium block">Primary Usage Type Name</label>
            <TextField
              size='small'
              id="primaryUsageTypeName"
              name="primaryUsageTypeName"
              value={formData.primaryUsageTypeName}
              onChange={handleInputChange}
              placeholder="Enter primary usage type name"
              disabled={isSubmitting}
              error={Boolean(errors.primaryUsageTypeName)}
              className={'w-full'}
            />
            {errors.primaryUsageTypeName && (
              <p className="text-sm text-red-500 mt-1">{errors.primaryUsageTypeName}</p>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={primaryUsageTypeId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddUpdateLocationPrimaryUsageTypeModal;